import COS from 'cos-js-sdk-v5';

import { CDN_PATH, COS_BUCKET, COS_REGION, DEFAULT_UUID, UPLOAD_PATH } from '@/const/cdn';
import { getNodeId } from '@/editor/flowEditor/control/utils/getNodeId';
import { getUserId } from '@/login/login';
import { putCos } from '@/put-cos';
import { StsType } from '@/services/cos';

/**
 * use download file
 * downloadFile(window.URL.createObjectURL(b64toBlob(fileData)), filename);
 * @param url
 * @param filename
 */
export const downloadFile = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
  a.click();
  a.remove();
};

export const downloadFileUrl = (name: string, url: string): void => {
  if (url) {
    fetch(url)
      .then(res => res.blob())
      .then((blob) => {
        downloadFile(window.URL.createObjectURL(blob), name);
      });
  }
};

// COS 图片水印参数
// https://cloud.tencent.com/document/product/460/6930
const IMAGE_WATERMARK = {
  image_key: `${UPLOAD_PATH}/watermark.png`,
  gravity: 'southwest',
  dx: 30,
  dy: 20,
  scatype: 2,
  spcent: 20,
};

// URL安全的 Base64编码
const urlSafeBase64Encode = (str: string): string => btoa(str)
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

export const downloadImageWatermark = (url: string, isHorizontal = false) => {
  if (!url) return;

  const watermark = {
    ...IMAGE_WATERMARK,
    scatype: isHorizontal ? 1 : 2,
  };

  // 动态生成水印URL片段
  const watermarkQuery = Object.entries(watermark)
    .map(([k, v]) => {
      switch (k) {
        case 'image_key':
          return `image_key/${urlSafeBase64Encode(v as string)}`;
        default:
          return `${k}/${v}`;
      }
    })
    .join('/');

  // 构建完整水印URL
  const watermarkUrl = `${url}?watermark/1/${watermarkQuery}`;

  const name = url.split('/').pop() || 'download';

  downloadFileUrl(name, watermarkUrl);
};

const COS_DOMAIN = 'nova.file.video.qpic.cn';

// COS 视频水印参数
// https://cloud.tencent.com/document/practice/436/101441
const VIDEO_WATERMARK = {
  Type: 'Image', // 水印类型  Image：图片水印
  Dx: '30', // 水平偏移
  Dy: '20', // 垂直偏移
  LocMode: 'Absolute', // 偏移方式 Relativity：按比例，Absolute：固定位置
  Pos: 'BottomLeft', // 基准位置 TopRight、TopLeft、BottomRight、BottomLeft、Left、Right、Top、Bottom、Center
  Image: { // 图片水印参数
    // Height: "2", // 高
    // Width: "2", // 宽
    Mode: 'Proportion', // 尺寸模式 1. Original：原有尺寸,2. Proportion：按比例,3. Fixed：固定大小
    Transparency: '90', // 透明度
    Background: 'false', // 是否为背景图
    Url: `${CDN_PATH}/${UPLOAD_PATH}/watermark.png`, // 水印图地址，支持GIF,PNG,MOV,APNG,JPG格式（gif和apng格式图片水印会在持续时间内循环播放）
  },
};

export const downloadVideoWatermark = (url: string, isHorizontal = false): Promise<void> => new Promise((resolve, reject) => {
  if (!url) {
    reject(new Error('URL is required'));
    return;
  }

  // 提取文件路径部分作为name
  const urlParts = url.split(`${COS_DOMAIN}/`);
  if (urlParts.length < 2) {
    reject(new Error('Invalid URL format'));
    return;
  }

  const path = urlParts[1];
  const uuid = getUserId() || DEFAULT_UUID;
  const newName = `${UPLOAD_PATH}/${uuid}/${getNodeId()}.mp4`; // 使用UUID生成新的文件名
  const watermark = {
    ...VIDEO_WATERMARK,
    Image: {
      ...VIDEO_WATERMARK.Image,
      ...(isHorizontal ? { Width: '6' } : { Height: '2' }),
    },
  };

  // 使用cos sdk 发起视频水印任务请求
  const body = COS.util.json2xml({
    Request: {
      Tag: 'Watermark', /* 创建任务的类型是 Watermark ,必须*/
      Input: {
        Object: path, /* 需要的视频文件，存储桶里的路径，必须 */
      },
      Operation: {
        Output: {
          Region: COS_REGION, /* 存储桶的地域，必须 */
          Bucket: COS_BUCKET, /* 存储结果的存储桶，必须 */
          Object: newName, /* 输出结果的文件名，必须 */
        },
        Watermark: [watermark],
      },
    },
  });

  const folderPath = path.split('/')?.[0] || 'cms_ai_video';
  const host = `${COS_BUCKET}.ci.${COS_REGION}.myqcloud.com`;
  const cos = putCos(folderPath, StsType.CI);
  cos.request(
    {
      Method: 'POST',
      Url: `https://${host}/jobs`,
      Key: '/jobs', /** 固定值，必须 */
      ContentType: 'application/xml', /** 固定值，必须 */
      Body: body,
    },
    (err, data) => {
      if (err) {
        reject(err);
        return;
      }
      const resp = data.Response?.JobsDetail || {};
      const jobId = resp.JobId;
      let watermarkUrl: string | null = '';
      const interval = setInterval(async () => {
        try {
          watermarkUrl = await getJobResult(cos, jobId, newName);

          if (watermarkUrl === null) {
            clearInterval(interval);
            reject(new Error('视频水印任务查询失败'));
          } else if (watermarkUrl !== '') {
            console.log('视频水印任务成功', watermarkUrl);
            clearInterval(interval);
            const name = watermarkUrl.split('/').pop() || 'download';
            downloadFileUrl(name, watermarkUrl);
            resolve();
          }
        } catch (error) {
          clearInterval(interval);
          reject(error);
        }
      }, 3000);
    },
  );
});

const getJobResult = async (cos: COS, jobId: string, name: string): Promise<string | null> => new Promise((resolve) => {
  const host = `${COS_BUCKET}.ci.${COS_REGION}.myqcloud.com`;
  cos.request(
    {
      Method: 'GET',
      Url: `https://${host}/jobs/${jobId}`,
      Key: `/jobs/${jobId}` /** 固定值，必须 */,
      ContentType: 'application/xml' /** 固定值，必须 */,
    },
    (err, data) => {
      if (err) {
        // 视频水印任务查询失败
        console.error(JSON.stringify(err));
        resolve(null);
      }
      const resp = data.Response?.JobsDetail || {};
      // 判断视频水印任务是否执行中
      if (resp.State !== 'Success') {
        resolve('');
      }
      // 任务执行完成，水印文件地址为
      const watermarkUrl = `${CDN_PATH}/${name}`;
      resolve(watermarkUrl);
    },
  );
});
